import adminService from '@/services/adminService';

// Initial state factory to ensure proper state initialization
const getInitialState = () => ({
  // Filter states
  selectedMonth: new Date().getMonth() + 1,
  selectedYear: new Date().getFullYear(),
  viewMode: 'daily', // 'daily', 'users', 'chart'
  
  // Data states
  dailyAnalytics: [],
  userAccounts: [],
  dailyAnalyticsRequestId: 0,
  summaryStats: {
    totalUsers: 0,
    activeUsers: 0,
    activeUsersPercentage: 0,
    newUsers7d: 0,
    dailyProfit: 0,
    monthlyProfit: 0,
    yearlyProfit: 0,
    totalCommission: 0,
    monthlyCommission: 0,
    yearlyCommission: 0,
    accountsUpdatedToday: 0,
    withdrawalsTodayCount: 0,
    withdrawalsTodayTotal: 0,
    lastBalanceUpdate: null
  },
  
  // Loading states
  isLoading: false,
  error: null
});

export default {
  namespaced: true,
  
  state: getInitialState(),
  
  mutations: {
    SET_FILTERS(state, { month, year }) {
      state.selectedMonth = month;
      state.selectedYear = year;
    },
    
    SET_VIEW_MODE(state, mode) {
      state.viewMode = mode;
    },
    
    SET_DAILY_ANALYTICS(state, data) {
      state.dailyAnalytics = Array.isArray(data) ? data : [];
    },

    SET_DAILY_ANALYTICS_REQUEST_ID(state, requestId) {
      state.dailyAnalyticsRequestId = requestId;
    },
    
    SET_USER_ACCOUNTS(state, data) {
      state.userAccounts = Array.isArray(data) ? data : [];
    },
    
    SET_SUMMARY_STATS(state, stats) {
      console.log('Setting summary stats with data:', stats);
      state.summaryStats = {
        totalUsers: stats.total_users || 0,
        activeUsers: stats.active_users || stats.active_accounts || 0,
        activeUsersPercentage: stats.active_users_percentage || 0,
        newUsers7d: stats.new_users_7d || 0,
        dailyProfit: stats.daily_profit || 0,
        monthlyProfit: stats.monthly_profit || 0,
        yearlyProfit: stats.yearly_profit || 0,
        totalCommission: stats.total_commission || 0,
        monthlyCommission: stats.monthly_commission || 0,
        yearlyCommission: stats.yearly_commission || 0,
        accountsUpdatedToday: stats.accounts_updated_today || stats.active_accounts || 0,
        withdrawalsTodayCount: stats.withdrawals_today_count || 0,
        withdrawalsTodayTotal: stats.withdrawals_today_total || 0,
        lastBalanceUpdate: stats.last_balance_update
      };
      console.log('Summary stats after setting:', state.summaryStats);
    },
    
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    }
  },
  
  actions: {
    async fetchDashboardSummary({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const data = await adminService.getDashboardSummary();
        console.log('Dashboard summary data received:', data);
        commit('SET_SUMMARY_STATS', data);
      } catch (error) {
        console.error('Error fetching dashboard summary:', error);
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchDailyAnalytics({ commit, state }, payload = {}) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const requestId = state.dailyAnalyticsRequestId + 1;
      commit('SET_DAILY_ANALYTICS_REQUEST_ID', requestId);
      
      try {
        const normalizedPayload = typeof payload === 'number'
          ? { days: payload }
          : (payload || {});

        const request = {
          ...normalizedPayload,
          month: normalizedPayload.month ?? state.selectedMonth,
          year: normalizedPayload.year ?? state.selectedYear
        };

        const data = await adminService.getDailyAnalytics(request);
        console.log('Daily analytics data:', data);
        // Ensure we always set an array
        const analyticsArray = Array.isArray(data) ? data : 
                              (data && data.results && Array.isArray(data.results)) ? data.results : 
                              [];
        console.log('Daily analytics processed array:', analyticsArray);
        
        // Log details about updated_users field
        if (analyticsArray.length > 0) {
          console.log('Sample daily record:', analyticsArray[0]);
          analyticsArray.forEach(day => {
            if (day.total_profit > 0 && (!day.updated_users || day.updated_users === 0)) {
              console.warn(`Day ${day.date} has profit ${day.total_profit} but updated_users is ${day.updated_users}`);
            }
          });
        }

        if (state.dailyAnalyticsRequestId !== requestId) {
          console.warn('Ignoring stale daily analytics response', { requestId, latest: state.dailyAnalyticsRequestId, request });
          return;
        }
        
        commit('SET_DAILY_ANALYTICS', analyticsArray);
      } catch (error) {
        console.error('Error fetching daily analytics:', error);
        if (state.dailyAnalyticsRequestId === requestId) {
          commit('SET_ERROR', error.message);
          commit('SET_DAILY_ANALYTICS', []); // Set empty array on error
        }
        throw error;
      } finally {
        if (state.dailyAnalyticsRequestId === requestId) {
          commit('SET_LOADING', false);
        }
      }
    },
    
    async fetchUserAccounts({ commit, state }, filters = {}) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const normalizedFilters = {
          ...filters,
          month: filters.month ?? state.selectedMonth,
          year: filters.year ?? state.selectedYear
        };

        const data = await adminService.getUserAccounts(normalizedFilters);
        console.log('User accounts data:', data);
        // Ensure we always set an array
        const accountsArray = Array.isArray(data) ? data : 
                             (data && data.results && Array.isArray(data.results)) ? data.results : 
                             [];
        commit('SET_USER_ACCOUNTS', accountsArray);
      } catch (error) {
        console.error('Error fetching user accounts:', error);
        commit('SET_ERROR', error.message);
        commit('SET_USER_ACCOUNTS', []); // Set empty array on error
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchAdminData({ dispatch, state }) {
      // Fetch all admin data
      const filters = {
        month: state.selectedMonth,
        year: state.selectedYear
      };

      await Promise.all([
        dispatch('fetchDashboardSummary'),
        dispatch('fetchDailyAnalytics', filters),
        dispatch('fetchUserAccounts', filters)
      ]);
    },
    
    updateFilters({ commit, dispatch }, filters) {
      commit('SET_FILTERS', filters);
      // Refetch data with new filters
      return dispatch('fetchAdminData');
    }
  },
  
  getters: {
    filteredUserAccounts: (state) => {
      return state.userAccounts.sort((a, b) => (b.amount_group || 0) - (a.amount_group || 0));
    },
    
    dailyProfitTrend: (state) => {
      return state.dailyAnalytics.map(day => ({
        date: day.date,
        profit: day.total_profit,
        transactions: day.transaction_count
      }));
    },
    
    activeVsInactiveRatio: (state) => {
      const { activeUsers, totalUsers } = state.summaryStats;
      return totalUsers > 0 ? (activeUsers / totalUsers * 100).toFixed(2) : 0;
    }
  }
};